import {
  createPersonalAvatar,
  CreatePersonalAvatarRequest,
  getPersonalAvatar,
  listPersonalAvatars,
  PersonalAvatarStatus,
  PersonalAvatarViewData,
} from '@/api/personal-avatars';
import { AI_SOURCES } from '@/constants/api';
import { Avatar, AvatarType } from '@/types/avatar';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback, useEffect } from 'react';
import { useCurrentApp } from '@/stores/userDataStore';

const mapPersonalAvatarToStudioVideoAvatar = (personalAvatar: PersonalAvatarViewData): Avatar | null => {
  if (!personalAvatar.trainedVoice || !personalAvatar.avatarImage) {
    return null;
  }

  return {
    id: personalAvatar.id,
    src: personalAvatar.avatarImage.imageUrl,
    posterSrc: personalAvatar.avatarImage.imageUrl,
    videoPreviewSrc: undefined,
    name: personalAvatar.name,
    source: AI_SOURCES.personal_avatar,
    approvedForAds: true,
    type: AvatarType.BunuPersonal,
    fullscreenFit: { type: 'cover' },
    avatarId: personalAvatar.id,
    bunuAvatarConfig: {
      avatarId: personalAvatar.id,
      voice: personalAvatar.trainedVoice,
      appKey: personalAvatar.appKey,
      avatarImageUrl: personalAvatar.avatarImage.imageUrl,
    },
  } satisfies Avatar;
};

export const useCreatePersonalAvatar = () => {
  const mutation = useMutation({
    mutationFn: async (createRequest: CreatePersonalAvatarRequest) => {
      const response = await createPersonalAvatar(createRequest);
      return response;
    },
  });

  return { createPersonalAvatar: mutation.mutateAsync, isPending: mutation.isPending };
};

export const transformPersonalAvatars = (avatars: PersonalAvatarViewData[]) => {
  return avatars?.map((avatar) => {
    return {
      ...avatar,
      type: AvatarType.Personal,
    };
  });
};
export const useListPersonalAvatars = ({ appKey, onlyDoneAvatars }: { appKey: string; onlyDoneAvatars?: boolean }) => {
  const { isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();
  const { data, isLoading } = useQuery({
    queryKey: ['personal-avatars', appKey],
    queryFn: () => listPersonalAvatars({ appKey }),
    select: (avatars) => {
      return transformPersonalAvatars(avatars);
    },
    enabled: !!appKey && isLoggedIn,
  });

  const personalAvatars = useMemo(() => {
    const avatars = data || [];
    return avatars.filter((avatar) => {
      return !onlyDoneAvatars || avatar.status === PersonalAvatarStatus.DONE;
    });
  }, [data, onlyDoneAvatars]);

  return { personalAvatars, isLoading: isLoadingUser || isLoading };
};

export const useStudioVideoAvatarsList = ({ appKey }: { appKey: string }) => {
  const { personalAvatars, isLoading } = useListPersonalAvatars({
    appKey,
    onlyDoneAvatars: true,
  });

  const avatars = useMemo(() => {
    return personalAvatars.reduce<Avatar[]>((acc, avatar) => {
      const studioAvatar = mapPersonalAvatarToStudioVideoAvatar(avatar);
      if (studioAvatar) {
        acc.push(studioAvatar);
      }

      return acc;
    }, []);
  }, [personalAvatars, appKey]);

  return { avatars, isLoading };
};

export const useGetStudioVideoAvatar = ({ appKey, avatarId }: { appKey: string; avatarId: string | null }) => {
  const { isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();
  const { data: personalAvatar, isLoading } = useQuery({
    queryKey: ['personal-avatar', appKey, avatarId],
    enabled: !!avatarId && !!appKey && isLoggedIn,
    queryFn: () => getPersonalAvatar({ appKey, id: avatarId! }),
  });

  const avatar = useMemo(() => {
    if (!personalAvatar) {
      return undefined;
    }

    return mapPersonalAvatarToStudioVideoAvatar(personalAvatar);
  }, [personalAvatar]);

  return { avatar, isLoading: isLoadingUser || isLoading };
};

export const usePersonalAvatarCache = ({ appKey }: { appKey: string }) => {
  const queryClient = useQueryClient();

  const updateCacheData = useCallback(
    (newItems: PersonalAvatarViewData[]) => {
      const itemsMap = newItems.reduce((map, item) => {
        return map.set(item.id, item);
      }, new Map<string, PersonalAvatarViewData>());

      queryClient.setQueryData<PersonalAvatarViewData[] | undefined>(['personal-avatars', appKey], (oldData) => {
        if (!oldData) return oldData;

        const updateData = oldData.map((oldAvatar) => {
          const newItem = itemsMap.get(oldAvatar.id);
          if (!newItem) {
            return oldAvatar;
          }

          return newItem;
        });

        return updateData;
      });
    },
    [queryClient, appKey]
  );

  const updatePersonalAvatarCache = useCallback(
    (personalAvatars: PersonalAvatarViewData[]) => {
      updateCacheData(personalAvatars);
    },
    [updateCacheData]
  );

  return {
    updatePersonalAvatarCache,
  };
};

export const usePrefetchPersonalAvatars = () => {
  const queryClient = useQueryClient();
  const { appKey, isLoggedIn } = useCurrentApp();
  const prefetchPersonalAvatars = useCallback(async () => {
    if (appKey && isLoggedIn) {
      queryClient.prefetchQuery({
        queryKey: ['personal-avatars', appKey],
        queryFn: () => listPersonalAvatars({ appKey }),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [appKey, isLoggedIn, queryClient]);

  useEffect(() => {
    prefetchPersonalAvatars();
  }, [prefetchPersonalAvatars]);

  return prefetchPersonalAvatars;
};
